import React, { useState, useEffect } from "react";
import { Button, Col, Form, message, Modal, Row, Skeleton } from "antd";

import { useFetchConstant } from "apis/constant";
import { constructDisplayPercentage, guard } from "utils/general";

import FormAutoComplete from "components/FormAutoComplete/FormAutoComplete";
import FormCheckbox from "components/FormCheckbox/FormCheckbox";
import FormInput from "components/FormInput/FormInput";
import FormSelection from "components/FormSelection/FormSelection";
import Locale from "react-intl-universal";

const { useForm } = Form;
const { confirm } = Modal;

const getCurrentResourceSuggestions = (investmentResources, currentType) => {
  const currentResources = investmentResources.find(
    (resource) => resource.value === currentType
  );
  return guard(
    () =>
      currentResources.suggestion.map((selection) => ({
        label: selection,
        key: selection,
        value: selection,
      })),
    []
  );
};

const AddResourceModal = ({
  visible,
  onOk,
  onCancel,
  onRemove,
  defaultValues,
}) => {
  const [form] = useForm();
  const [currentType, setCurrentType] = useState("");
  const [selections, setSelections] = useState([]);

  const {
    isLoading: isInvestmentResourcesLoading,
    selection: investmentResources,
    data: investmentResourcesConst,
  } = useFetchConstant("investmentResources");

  const isCurrentTypeLifeInsurance =
    !isInvestmentResourcesLoading &&
    currentType === investmentResourcesConst.LIFE_INSURANCE.code;
  const isCurrentTypeRealProperty =
    !isInvestmentResourcesLoading &&
    currentType === investmentResourcesConst.REAL_PROPERTY.code;
  const isCurrentTypeCash =
    !isInvestmentResourcesLoading &&
    currentType === investmentResourcesConst.CASH.code;

  useEffect(() => {
    if (defaultValues && defaultValues.type) {
      setCurrentType(defaultValues.type);
    }
  }, [defaultValues]);

  const getInitialValues = () => {
    if (defaultValues) {
      return {
        ...defaultValues,
        returnRate: constructDisplayPercentage(
          defaultValues.returnRate || 0,
          false
        ),
      };
    }
  };

  const handleOnOkButtonClick = async (e) => {
    e.preventDefault();
    try {
      const values = await form.validateFields();

      const formattedValues = {
        ...values,
        returnRate: Number(values.returnRate) / 100,
      };

      onOk(formattedValues);
    } catch (error) {
      error.errorFields.forEach((field) => message.error(field.errors[0]));
    }
  };

  const handleOnRemoveButtonClick = () => {
    confirm({
      title: Locale.get("investmentPortfolio.resource.confirmMsgResource"),
      okText: Locale.get("investmentPortfolio.resource.yes"),
      onOk() {
        onRemove();
      },
      onClose() {},
    });
  };

  const handleOnSearchResourceTypes = (inputValue) => {
    const currentResourceSuggestions = getCurrentResourceSuggestions(
      investmentResources,
      currentType
    );
    const newSelections = inputValue
      ? currentResourceSuggestions.filter((suggestion) => {
          const upperCaseSuggestionValue = suggestion.value.toUpperCase();
          return upperCaseSuggestionValue.includes(inputValue.toUpperCase());
        })
      : [];

    setSelections(newSelections);
  };

  const footer = defaultValues
    ? [
        <Button key="remove" onClick={handleOnRemoveButtonClick}>
          {Locale.get("investmentPortfolio.resource.removeResource")}
        </Button>,
        <Button key="submit" type="primary" onClick={handleOnOkButtonClick}>
          {Locale.get("investmentPortfolio.resource.saveResource")}
        </Button>,
      ]
    : [
        <Button key="cancel" onClick={onCancel}>
          {Locale.get("investmentPortfolio.resource.cancelResource")}
        </Button>,
        <Button key="submit" type="primary" onClick={handleOnOkButtonClick}>
          {Locale.get("investmentPortfolio.addNewResource")}
        </Button>,
      ];

  return (
    <Modal
      title={Locale.get("investmentPortfolio.resource.resourceDetail")}
      visible={visible}
      onOk={handleOnOkButtonClick}
      onCancel={onCancel}
      footer={footer}
    >
      {isInvestmentResourcesLoading ? (
        <Skeleton active />
      ) : (
        <Form form={form} initialValues={getInitialValues()}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <FormSelection
                label={Locale.get("investmentPortfolio.resource.resourceCat")}
                name="type"
                placeholder={Locale.get(
                  "investmentPortfolio.resource.resourceCatPlaceholder"
                )}
                requiredErrorMessage={Locale.get(
                  "investmentPortfolio.resource.errMsgResourceCat"
                )}
                selections={investmentResources}
                extraProps={{ onChange: setCurrentType }}
                defaultActiveFirstOption
              />
            </Col>
            <Col span={24}>
              <FormAutoComplete
                label={Locale.get(
                  "investmentPortfolio.resource.resourceLabelRetirementEducation"
                )}
                name="label"
                requiredErrorMessage={Locale.get(
                  "investmentPortfolio.resource.errMsgResourceLabel"
                )}
                placeholder={Locale.get(
                  "investmentPortfolio.resource.resourceLabelPlaceholder"
                )}
                isDisabled={!currentType}
                selections={selections}
                onSearch={handleOnSearchResourceTypes}
                defaultActiveFirstOption
              />
            </Col>
            <Col
              span={isCurrentTypeLifeInsurance ? 24 : 12}
              style={{ height: 100 }}
            >
              <FormInput
                label={
                  isCurrentTypeLifeInsurance
                    ? Locale.get("investmentPortfolio.futureValueRetirement")
                    : Locale.get(
                        "investmentPortfolio.resource.estimatedCurrentValue"
                      )
                }
                name="currentValue"
                extraProps={{
                  type: "number",
                  prefix: "RM",
                  onPressEnter: handleOnOkButtonClick,
                }}
                disabled={!currentType}
                requiredErrorMessage={Locale.get(
                  "investmentPortfolio.resource.errMsgValue"
                )}
              />
            </Col>
            {!isCurrentTypeLifeInsurance && (
              <>
                <Col span={12} style={{ height: 100 }}>
                  <FormInput
                    label={Locale.get("investmentPortfolio.ror")}
                    name="returnRate"
                    extraProps={{
                      type: "number",
                      suffix: "%",
                      onPressEnter: handleOnOkButtonClick,
                    }}
                    disabled={!currentType}
                    requiredErrorMessage={Locale.get(
                      "investmentPortfolio.errMsgRor"
                    )}
                  />
                </Col>
                {!isCurrentTypeRealProperty && !isCurrentTypeCash && (
                  <Col span={24} style={{ height: 40 }}>
                    <FormCheckbox name="isContinuousContribution">
                      {Locale.get(
                        "investmentPortfolio.resource.contributionCheckbox"
                      )}
                    </FormCheckbox>
                  </Col>
                )}
              </>
            )}
            {isCurrentTypeRealProperty && (
              <Col span={24}>
                <FormInput
                  label={Locale.get("investmentPortfolio.outstandingDebt")}
                  name="outstandingDebt"
                  extraProps={{
                    type: "number",
                    prefix: "RM",
                    onPressEnter: handleOnOkButtonClick,
                  }}
                  disabled={!currentType}
                />
              </Col>
            )}
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default AddResourceModal;
